<template>
  <div class="page-wrap">
    <!-- 筛选栏 -->
    <el-card class="filter-card" shadow="never">
      <el-form :model="filterForm" inline>
        <el-form-item label="真实姓名">
          <el-input v-model="filterForm.realName" placeholder="真实姓名" clearable style="width:120px" />
        </el-form-item>
        <el-form-item label="学科">
          <el-select v-model="filterForm.subjectId" placeholder="全部" clearable style="width:120px">
            <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="试卷">
          <el-select
            v-model="filterForm.paperId"
            placeholder="全部试卷"
            clearable
            filterable
            remote
            reserve-keyword
            :remote-method="searchPapers"
            :loading="paperLoading"
            style="width:280px"
          >
            <el-option
              v-for="p in filteredPapers"
              :key="p.id"
              :label="p.name"
              :value="p.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="错误时间">
          <el-date-picker
            v-model="filterForm.timeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width:400px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="onFilterChange">搜索</el-button>
          <el-button @click="resetFilter">重置</el-button>
          <el-button type="danger" icon="el-icon-download" @click="exportPdf" :loading="exporting">
            {{ exportButtonText }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 试卷维度列表 -->
    <el-card shadow="never" v-if="!showDetail">
      <div class="table-toolbar">
        <span class="toolbar-title">错题试卷列表</span>
        <el-button 
          type="primary" 
          size="small" 
          icon="el-icon-download" 
          @click="exportMergedPapers" 
          :disabled="selectedPaperKeys.length === 0"
          :loading="exporting"
        >
          合并导出 ({{ selectedPaperKeys.length }})
        </el-button>
      </div>
      <el-table :data="paperStats" border stripe v-loading="loading" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column type="index" width="60" label="#" />
        <el-table-column label="姓名" width="120">
          <template #default="{ row }">
            {{ row.realName || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="试卷名称" min-width="200">
          <template #default="{ row }">
            <el-link type="primary" @click="viewPaperDetail(row)">{{ row.paperName }}</el-link>
          </template>
        </el-table-column>
        <el-table-column label="学科" width="120">
          <template #default="{ row }">
            {{ getSubjectName(row.subjectId) }}
          </template>
        </el-table-column>
        <el-table-column label="题总数" prop="totalCount" width="90" align="center" />
        <el-table-column label="错题数量" prop="wrongCount" width="100" align="center" />
        <el-table-column label="错题率" prop="wrongRate" width="90" align="center" />
        <el-table-column label="最近错误时间" prop="lastWrongTime" width="170" />
        <el-table-column label="操作" width="150" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="mini" @click="viewPaperDetail(row)">查看错题</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 试卷详情（具体错题列表） -->
    <el-card shadow="never" v-else>
      <div class="table-toolbar">
        <el-button size="small" @click="backToList">← 返回试卷列表</el-button>
        <span class="toolbar-title">{{ currentPaper.paperName }} - 错题详情</span>
        <el-button type="danger" size="small" icon="el-icon-download" @click="exportCurrentPaperAll" :loading="exporting">
          导出本卷全部错题
        </el-button>
        <el-button size="small" @click="exportSelected" :disabled="!canExportSelected" :loading="exporting">
          导出当前选中题目
        </el-button>
      </div>
      <el-table
        :data="tableData"
        border
        stripe
        v-loading="loading"
        highlight-current-row
        :row-class-name="detailRowClassName"
        @row-click="handleDetailRowClick"
      >
        <el-table-column label="选择" width="55" align="center">
          <template slot-scope="scope">
            <el-radio v-model="selectedIndex" :label="scope.$index" @click.native.stop="selectDetailRow(scope.$index)">&nbsp;</el-radio>
          </template>
        </el-table-column>
        <el-table-column label="题号" type="index" width="60" />
        <el-table-column label="题目内容" min-width="500">
          <template #default="{ row }">
            <div class="question-detail">
              <!-- 题型标签 -->
              <div class="question-type-tag">
                <span :class="['type-tag', row.question && row.question.type]">{{ getQuestionType(row.question && row.question.type) }}</span>
              </div>
              <!-- 题干 -->
              <div class="question-content rich-html" v-html="renderHtml(row.question && row.question.content)"></div>
              <!-- 选项（单选/多选） -->
              <div v-if="row.question && (row.question.type === 'SINGLE' || row.question.type === 'MULTI')" class="options-list">
                <div v-for="opt in parseOptions(row.question && row.question.optionsJson)" :key="opt.key" class="option-item">
                  <span class="option-key">{{ opt.key }}</span>
                  <div class="option-value rich-html" v-html="renderHtml(opt.value)"></div>
                </div>
              </div>
              <!-- 正确答案 -->
              <div class="correct-answer">
                <span class="label">正确答案：</span>
                <span class="value">{{ row.question && row.question.answer }}</span>
              </div>
              <!-- 解析 -->
              <div v-if="row.question && row.question.analysis" class="analysis-box">
                <div class="analysis-title">解析</div>
                <div class="analysis-content rich-html" v-html="renderHtml(row.question && row.question.analysis)"></div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="错误次数" prop="wrongCount" width="100" align="center" />
        <el-table-column label="最近错误" prop="lastWrongTime" width="170" />
      </el-table>
      <el-pagination
        class="pagination"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        :current-page="page + 1"
        :page-sizes="[10, 20, 50]"
        :page-size="size"
        :total="total"
        layout="total, sizes, prev, pager, next"
      />
    </el-card>
  </div>
</template>

<script>
import request from '../../utils/request'
import filterLoader from '../../mixins/filterLoader'
import { renderHtml } from '../../utils/renderHtml'

export default {
  name: 'WrongList',
  mixins: [filterLoader],
  data() {
    return {
      filterForm: { realName: null, subjectId: null, paperId: null, userId: null, timeRange: [] },
      subjects: [],
      papers: [],
      filteredPapers: [], // 搜索过滤后的试卷列表
      paperLoading: false, // 试卷搜索加载状态
      paperStats: [], // 试卷维度统计
      tableData: [], // 当前试卷的错题详情
      selectedIndex: -1,
      selectedPaper: null,
      selectedPaperId: null,
      selectedPaperKey: null,
      selectedPaperKeys: [],
      showDetail: false, // 是否显示详情
      currentPaper: {}, // 当前查看的试卷
      page: 0,
      size: 10,
      total: 0,
      loading: false,
      exporting: false
    }
  },
  created() {
    this.loadSubjectsFromFilters('subjects')
    this.loadPaperList('papers').then(() => {
      // 初始化时显示所有试卷
      this.filteredPapers = this.papers
    })
    this.loadPaperStats()
  },
  computed: {
    selectedRow() {
      if (this.selectedIndex < 0) return null
      return this.tableData[this.selectedIndex] || null
    },
    canExportSelected() {
      return this.selectedIndex >= 0 && this.selectedRow != null
    },
    exportButtonText() {
      if (this.showDetail) {
        return '导出本卷全部错题'
      } else if (!this.showDetail && this.selectedPaper) {
        return `导出「${this.selectedPaper.paperName}」`
      } else if (this.filterForm.realName) {
        return `导出 ${this.filterForm.realName} 的错题试卷`
      }
      return '导出错题试卷'
    }
  },
  watch: {
    selectedPaperKey(newVal) {
      if (newVal) {
        const [userId, paperId] = newVal.split('_')
        this.selectedPaper = this.paperStats.find(item => 
          String(item.userId) === userId && String(item.paperId) === paperId
        )
        this.selectedPaperId = paperId ? Number(paperId) : null
      } else {
        this.selectedPaper = null
        this.selectedPaperId = null
      }
    }
  },
  methods: {
    renderHtml,
    buildParams() {
      const { realName, subjectId, paperId, timeRange, userId } = this.filterForm
      // paperId 为 -1 表示"未分类"，需要传递特殊值给后端
      let paperParam = paperId
      if (paperId === -1 || paperId === '-1') {
        paperParam = -1  // 后端用 -1 表示查询未分类的错题
      } else if (!paperId) {
        paperParam = undefined
      }
      return {
        page: this.page,
        size: this.size,
        realName: realName || undefined,
        subjectId: subjectId || undefined,
        paperId: paperParam,
        userId: userId || undefined,  // 添加 userId 过滤
        startTime: timeRange && timeRange[0] ? timeRange[0] : undefined,
        endTime: timeRange && timeRange[1] ? timeRange[1] : undefined
      }
    },
    getSubjectName(subjectId) {
      if (!subjectId) return ''
      const subject = this.subjects.find(s => s.id === subjectId)
      return subject ? subject.name : ''
    },
    // 搜索试卷
    searchPapers(keyword) {
      if (!keyword || keyword.trim() === '') {
        this.filteredPapers = this.papers
        return
      }
      this.paperLoading = true
      const lowerKeyword = keyword.toLowerCase()
      this.filteredPapers = this.papers.filter(p =>
        p.name && p.name.toLowerCase().includes(lowerKeyword)
      )
      this.paperLoading = false
    },
    getQuestionType(type) {
      const typeMap = { SINGLE: '单选', MULTI: '多选', FILL: '填空', QA: '问答' }
      return typeMap[type] || type
    },
    // 解析选项JSON
    parseOptions(optionsJson) {
      if (!optionsJson) return []
      try {
        const parsed = JSON.parse(optionsJson)
        if (Array.isArray(parsed) && parsed.length > 0) {
          // 新格式：[{key: "A", value: "选项A"}, ...]
          if (typeof parsed[0] === 'object' && parsed[0].key) {
            return parsed
          }
          // 旧格式：["A.选项A", "B.选项B"]
          if (typeof parsed[0] === 'string') {
            return parsed.map(str => {
              const match = str.match(/^([A-D])\.(.+)$/)
              if (match) {
                return { key: match[1], value: match[2].trim() }
              }
              return { key: str.charAt(0), value: str }
            })
          }
        }
        return []
      } catch (e) {
        return []
      }
    },
    // 加载试卷维度统计
    loadPaperStats() {
      this.loading = true
      request.get('/wrong/paperStats', { params: this.buildParams() })
        .then(data => {
          this.paperStats = data || []
          this.selectedPaper = null
          this.selectedPaperId = null
        }).finally(() => { this.loading = false })
    },
    // 查看试卷详情
    viewPaperDetail(paper) {
      this.currentPaper = paper
      this.showDetail = true
      this.page = 0
      this.filterForm.paperId = paper.paperId
      // 关键修复：同时设置 userId，确保只查询该用户的错题
      this.filterForm.userId = paper.userId
      this.loadWrongQuestions()
    },
    // 返回列表
    backToList() {
      this.showDetail = false
      this.currentPaper = {}
      this.filterForm.paperId = null
      this.filterForm.userId = null  // 清除 userId 过滤
      this.selectedIndex = -1
      this.selectedPaperKey = null
      this.loadPaperStats()
    },
    // 加载具体错题
    loadWrongQuestions() {
      this.loading = true
      request.get('/wrong/list', { params: this.buildParams() })
        .then(data => {
          this.tableData = this.normalizeWrongRows(data.content || [])
          this.total = data.totalElements || 0
          this.selectedIndex = -1
        }).finally(() => { this.loading = false })
    },
    loadList() {
      if (this.showDetail) {
        this.loadWrongQuestions()
      } else {
        this.loadPaperStats()
      }
    },
    onFilterChange() {
      this.page = 0
      if (this.showDetail) {
        this.loadWrongQuestions()
      } else {
        this.loadPaperStats()
      }
    },
    resetFilter() {
      this.filterForm = { realName: null, subjectId: null, paperId: null, userId: null, timeRange: [] }
      this.selectedIndex = -1
      this.selectedPaper = null
      this.selectedPaperId = null
      this.selectedPaperKey = null
      this.selectedPaperKeys = []
      if (this.showDetail) {
        this.backToList()
      } else {
        this.onFilterChange()
      }
    },
    handleSelectionChange(selection) {
      this.selectedPaperKeys = selection
    },
    handlePageChange(val) { this.page = val - 1; this.loadList() },
    handleSizeChange(val) { this.size = val; this.page = 0; this.loadList() },
    normalizeWrongRows(rows) {
      return (rows || []).map(row => ({
        ...row,
        id: row.id != null ? row.id : row.wrongQuestionId
      }))
    },
    resolveSelectedExportRequest(row) {
      const wrongId = row && row.id
      if (wrongId != null && wrongId !== '') {
        const params = new URLSearchParams()
        params.append('ids', String(wrongId))
        return { path: '/api/admin/wrong/exportPdfByIds', params }
      }
      const questionId = row && row.question && row.question.id
      const userId = this.filterForm.userId || (this.currentPaper && this.currentPaper.userId)
      const paperId = (this.currentPaper && this.currentPaper.paperId) || this.filterForm.paperId
      if (questionId && userId) {
        const params = new URLSearchParams()
        params.append('userId', String(userId))
        params.append('questionId', String(questionId))
        if (paperId) params.append('paperId', String(paperId))
        return { path: '/api/admin/wrong/exportPdfBySelection', params }
      }
      return null
    },
    runExportRequest(request, filename) {
      const token = localStorage.getItem('admin_token')
      return fetch(`${request.path}?${request.params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).then(async res => {
        const contentType = res.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const err = await res.json()
          this.$message.error(err.message || '导出失败')
          return
        }
        if (!res.ok) {
          this.$message.error('导出失败，请稍后重试')
          return
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
      })
    },
    handleDetailRowClick(row) {
      const index = this.tableData.indexOf(row)
      if (index >= 0) this.selectedIndex = index
    },
    selectDetailRow(index) {
      this.selectedIndex = index
    },
    detailRowClassName({ rowIndex }) {
      return this.selectedIndex === rowIndex ? 'detail-row-selected' : ''
    },
    exportSelected() {
      if (!this.canExportSelected) {
        this.$message.warning('请先点击题目行选中要导出的题目')
        return
      }
      const request = this.resolveSelectedExportRequest(this.selectedRow)
      if (!request) {
        this.$message.error('无法识别选中题目，请刷新后重试')
        return
      }
      this.exporting = true
      this.runExportRequest(request, `选中错题_${new Date().toLocaleDateString()}.pdf`)
        .catch(() => { this.$message.error('导出失败') })
        .finally(() => { this.exporting = false })
    },
    exportCurrentPaperAll() {
      const paper = this.currentPaper || {}
      if (!paper.paperId) {
        this.$message.warning('缺少试卷信息')
        return
      }
      this.exporting = true
      const token = localStorage.getItem('admin_token')
      const params = new URLSearchParams()
      params.append('paperId', paper.paperId)
      if (paper.userId) params.append('userId', paper.userId)
      if (paper.realName) params.append('realName', paper.realName)

      fetch(`/api/admin/wrong/exportPdf?${params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).then(async res => {
        const contentType = res.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const err = await res.json()
          this.$message.error(err.message || '导出失败')
          return
        }
        if (!res.ok) {
          this.$message.error('导出失败，请稍后重试')
          return
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${paper.paperName || '错题试卷'}_全部错题.pdf`
        a.click()
        URL.revokeObjectURL(url)
      }).catch(err => {
        console.error('导出失败:', err)
        this.$message.error('导出失败: ' + (err.message || '网络错误'))
      }).finally(() => { this.exporting = false })
    },
    exportPdf() {
      // 详情页：导出本卷全部错题（不是只导出选中的那一道）
      if (this.showDetail) {
        this.exportCurrentPaperAll()
        return
      }
      
      // 如果选中了试卷（通过复选框），导出选中的第一个试卷
      if (this.selectedPaperKeys.length > 0) {
        const paper = this.selectedPaperKeys[0]
        this.exporting = true
        const token = localStorage.getItem('admin_token')
        const params = new URLSearchParams()
        params.append('paperId', paper.paperId)
        // 必须传递 userId 和 realName，确保只导出该用户该试卷的错题
        if (paper.userId) {
          params.append('userId', paper.userId)
        }
        if (paper.realName) {
          params.append('realName', paper.realName)
        }

        fetch(`/api/admin/wrong/exportPdf?${params.toString()}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }).then(async res => {
          const contentType = res.headers.get('content-type') || ''
          if (contentType.includes('application/json')) {
            const err = await res.json()
            this.$message.error(err.message || '导出失败')
            return
          }
          if (!res.ok) {
            this.$message.error('导出失败，请稍后重试')
            return
          }
          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `${paper.paperName}_错题试卷.pdf`
          a.click()
          URL.revokeObjectURL(url)
        }).catch(err => {
          console.error('导出失败:', err)
          this.$message.error('导出失败: ' + (err.message || '网络错误'))
        }).finally(() => { this.exporting = false })
        return
      }
      
      // 否则导出筛选条件下的全部
      this.exporting = true
      const token = localStorage.getItem('admin_token')
      const { realName, subjectId, paperId, timeRange } = this.filterForm
      const params = new URLSearchParams()
      if (realName) params.append('realName', realName)
      if (subjectId) params.append('subjectId', subjectId)
      if (paperId) params.append('paperId', paperId)
      if (timeRange && timeRange[0]) params.append('startTime', timeRange[0])
      if (timeRange && timeRange[1]) params.append('endTime', timeRange[1])

      fetch(`/api/admin/wrong/exportPdf?${params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).then(async res => {
        const contentType = res.headers.get('content-type') || ''
        console.log('导出响应类型:', contentType)
        // 检查响应类型，如果是JSON则显示错误信息
        if (contentType.includes('application/json')) {
          const err = await res.json()
          this.$message.error(err.message || '导出失败')
          return
        }
        if (!res.ok) {
          this.$message.error('导出失败，请稍后重试')
          return
        }
        const blob = await res.blob()
        console.log('导出文件大小:', blob.size)
        if (blob.size === 0) {
          this.$message.error('导出的文件为空')
          return
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const filename = this.currentPaper.paperName 
          ? `${this.currentPaper.paperName}错题试卷.pdf`
          : `错题试卷_${new Date().toLocaleDateString()}.pdf`
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
      }).catch(err => {
        console.error('导出失败:', err)
        this.$message.error('导出失败: ' + (err.message || '网络错误'))
      }).finally(() => { this.exporting = false })
    },
    exportMergedPapers() {
      if (this.selectedPaperKeys.length === 0) {
        this.$message.warning('请先选择要导出的试卷')
        return
      }
      this.exporting = true
      const token = localStorage.getItem('admin_token')
      
      // 构建参数：传递选中的用户ID和试卷ID列表
      const params = new URLSearchParams()
      const userPaperPairs = this.selectedPaperKeys.map(item => `${item.userId}:${item.paperId}`).join(',')
      params.append('userPaperPairs', userPaperPairs)

      fetch(`/api/admin/wrong/exportMergedPapers?${params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).then(async res => {
        const contentType = res.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const err = await res.json()
          this.$message.error(err.message || '导出失败')
          return
        }
        if (!res.ok) {
          this.$message.error('导出失败，请稍后重试')
          return
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        // 合并导出文件名格式：合并试卷-错题+时间
        const dateStr = new Date().toLocaleDateString().replace(/\//g, '-')
        a.download = `合并试卷-错题${dateStr}.pdf`
        a.click()
        URL.revokeObjectURL(url)
        this.$message.success('合并导出成功')
      }).catch(err => {
        console.error('导出失败:', err)
        this.$message.error('导出失败: ' + (err.message || '网络错误'))
      }).finally(() => { this.exporting = false })
    }
  }
}
</script>

<style scoped>
.filter-card { margin-bottom: 16px; }
.pagination { margin-top: 16px; text-align: right; }
.table-toolbar {
  margin-bottom: 16px;
  display: flex;
  gap: 10px;
  align-items: center;
}
:deep(.el-table__body tr) {
  cursor: pointer;
}
:deep(.detail-row-selected > td) {
  background-color: #ecf5ff !important;
}
.toolbar-title {
  font-size: 16px;
  font-weight: bold;
  flex: 1;
}

/* 题目详情样式 */
.question-detail {
  padding: 10px 0;
}
.question-type-tag {
  margin-bottom: 10px;
}
.type-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
}
.type-tag.SINGLE { background: #e6f7ff; color: #1890ff; }
.type-tag.MULTI { background: #fff7e6; color: #fa8c16; }
.type-tag.FILL { background: #f6ffed; color: #52c41a; }
.type-tag.QA { background: #fff1f0; color: #f5222d; }

.question-content {
  font-size: 15px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 15px;
}

.options-list {
  margin: 15px 0;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 8px;
}
.option-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}
.option-item:last-child {
  border-bottom: none;
}
.option-key {
  width: 28px;
  height: 28px;
  background: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
}
.option-value {
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.correct-answer {
  margin: 15px 0;
  padding: 12px 15px;
  background: #f0f5ff;
  border-radius: 8px;
  border-left: 4px solid #1677ff;
}
.correct-answer .label {
  font-size: 14px;
  color: #666;
}
.correct-answer .value {
  font-size: 16px;
  color: #1677ff;
  font-weight: 600;
  margin-left: 8px;
}

.analysis-box {
  margin-top: 15px;
  padding: 15px;
  background: #f6ffed;
  border-radius: 8px;
  border-left: 4px solid #52c41a;
}
.analysis-title {
  font-size: 14px;
  color: #52c41a;
  font-weight: 600;
  margin-bottom: 10px;
}
.analysis-content {
  font-size: 14px;
  line-height: 1.8;
  color: #333;
}

/* 图片样式 */
.question-content :deep(img),
.option-value :deep(img),
.analysis-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
  margin: 6px 0;
}
</style>
